<template>
  <div
    role="dialog"
    id="radix-_r_2i_"
    aria-describedby="radix-_r_2k_"
    aria-labelledby="radix-_r_2j_"
    data-state="open"
    class="grid w-full gap-4 p-6"
    tabindex="-1"
    style="pointer-events: auto"
    v-loading="loading"
  >
    <div class="flex flex-col space-y-1.5 text-center sm:text-left">
      <h2
        id="radix-_r_2j_"
        class="text-lg font-semibold leading-none tracking-tight"
      >
        {{ title }}
      </h2>
      <p id="radix-_r_2k_" class="text-sm text-muted-foreground">
        Update recipient information
      </p>
    </div>
    <div class="space-y-4 py-4">
      <div class="space-y-2">
        <label
          class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          for="edit-recipient-name"
          >Full Name</label
        >
        <el-input
          id="edit-recipient-name"
          placeholder="e.g., John Smith"
          v-model="recipient.name"
        />
      </div>
      <div class="space-y-2">
        <label
          class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          for="edit-recipient-phone"
        >
          Phone Number
        </label>
        <el-input
          id="edit-recipient-phone"
          placeholder="e.g., +1234567890"
          v-model="recipient.phone"
        />
        <p class="text-xs text-muted-foreground">
          Include country code (e.g., +1 for US)
        </p>
      </div>
      <div class="space-y-2">
        <label
          class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          for="edit-recipient-role"
          >Role</label
        >
        <SmartSelect
          id="edit-recipient-role"
          v-model="recipient.role"
          :options="['Staff', 'Supervision', 'Director']"
          placeholder="Select role"
          class="w-full"
        />
      </div>
      <div
        data-orientation="horizontal"
        role="none"
        class="shrink-0 bg-border h-px w-full"
      ></div>
      <div class="space-y-2">
        <label
          class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >Status</label
        >
        <div class="flex items-center gap-4">
          <button
            @click="recipient.status = 'Active'"
            :class="{
              'bg-accent text-accent-foreground': recipient.status === 'Active',
            }"
            class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input hover:bg-accent hover:text-accent-foreground border h-9 rounded-md px-3 flex-1 bg-transparent"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-circle-check-big mr-2 h-4 w-4 text-green-600"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <path d="m9 11 3 3L22 4"></path>
            </svg>
            Active</button
          ><button
            @click="recipient.status = 'Inactive'"
            :class="{
              'bg-accent text-accent-foreground':
                recipient.status === 'Inactive',
            }"
            class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input hover:bg-accent hover:text-accent-foreground border h-9 rounded-md px-3 flex-1 bg-transparent"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-circle-x mr-2 h-4 w-4 text-red-600"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <path d="m15 9-6 6"></path>
              <path d="m9 9 6 6"></path>
            </svg>
            Inactive
          </button>
        </div>
      </div>
    </div>
    <div class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
      <button
        @click="$emit('cancel')"
        class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-10 px-4 py-2"
      >
        Cancel
      </button>
      <el-button :loading="submitting" type="primary" @click="handleSubmit">
        Save Changes
        {{ id == null ? "Add Recipient" : "Save Changes" }}
      </el-button>
    </div>
    <button
      @click="$emit('cancel')"
      type="button"
      class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="lucide lucide-x h-4 w-4"
      >
        <path d="M18 6 6 18"></path>
        <path d="m6 6 12 12"></path>
      </svg>
      <span class="sr-only">Close</span>
    </button>
  </div>
</template>

<script>
import {
  createNotificationRecipient,
  updateNotificationRecipient,
  getNotificationRecipient,
} from "@/api";
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";

export default {
  name: "EditRecipient",
  components: {
    SmartSelect,
  },
  props: {
    id: {
      type: [Number, null],
      default: null,
    },
    title: {
      type: String,
      default: "Edit Recipient",
    },
  },
  data() {
    return {
      recipient: {
        name: "",
        phone: "",
        role: "Staff",
        status: "Active",
      },
      loading: false,
      submitting: false,
    };
  },
  created() {
    if (this.id) {
      this.loadRecipientData();
    }
  },
  methods: {
    async loadRecipientData() {
      this.loading = true;
      try {
        const response = await getNotificationRecipient(this.id);
        this.recipient = {
          ...response,
          role: response.role || "Staff",
          status: response.status || "Active",
        };
      } catch (error) {
        this.$message.error("Failed to load recipient data.");
      } finally {
        this.loading = false;
      }
    },
    async handleSubmit() {
      this.submitting = true;
      try {
        if (this.id) {
          await updateNotificationRecipient({
            id: this.id,
            ...this.recipient,
          });
          this.$message.success("Recipient updated successfully.");
        } else {
          await createNotificationRecipient(this.recipient);
          this.$message.success("Recipient created successfully.");
        }
        this.$emit("confirm");
      } catch (error) {
        this.$message.error("Failed to save recipient.");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
